import React, { useEffect, useRef, useState } from "react";
import CalebAndBrownLogo from "src/icons/cnbDesktop";
import { useHistory } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
import StylisedButton, { StylisedButtonStyle } from "./StylisedButton";
import StylisedInputField from "./StylisedInputField";
import {
  FormWrapper,
  FormFrame,
  FormHeader,
  FormContent,
  FormHeaderTextWrapper,
  FormHeaderText,
  FormHeaderButton,
  FormContentTitle,
  FormContentFields,
  FormContentTitleAndFields,
  FormContentTitleAndFieldsWrapper,
  FormContentTitleAndFieldsMobile,
  FormCallsToActionWrapper,
  FormContentInputFieldWrapperStretch,
  FormContentInputFieldError,
  FormContentInputFieldErrorText,
  ForgotPasswordWrapper,
  FormContentLeftAligned,
  MultiAccountDescription,
  MultiAccountOptionsContainer,
  MultiAccountOptionButton,
  MultiAccountOptionContent,
  MultiAccountOptionText,
  MultiAccountOptionName,
  MultiAccountOptionId,
  TwoFASubtitle,
  LoadingSpinnerContainer,
  FormHeaderTextMobile,
  FormFrameMobile,
} from "../styles/LandingPageForm.styles";
import { LoadingSpinner } from "src/components/application/LoadingSpinner";
import MagicLinkForm from "src/components/auth/MagicLinkForm";
import PublicIntercom from "src/components/core/PublicIntercom";
import { GetMultiAccountUsersApiResponse } from "src/store/features/api/generated";
import { setNumberOfAccounts } from "src/store/features/settings.slice";
import { useAppDispatch as useAppDispatchStore } from "src/store/store";
import { setIsMultiAccountComponentRendered } from "src/store/features/onboarding.slice";
import { useLogin } from "../hooks/useLogin";
import TurnstileWidget from "./TurnstileWidget";
import { useLoginFormInputSlice } from "src/store/features/loginFormInput.slice";
import { useOnboarding } from "src/store/features/onboarding.slice";
import EyeClosedIcon from "../icons/EyeClosedIcon";
import EyeOpenIcon from "../icons/EyeOpenIcon";
import EnvelopeIcon from "../icons/EnvelopeIcon";

const FormHeaderComponent = () => {
  const isMobile = useIsMobile();
  const { isMultiAccountComponentRendered } = useOnboarding();

  const HeaderButton = () => {
    return (
      <FormHeaderButton
        onClick={() => {
          window.location.href = "/signup";
        }}>
        Sign up
      </FormHeaderButton>
    );
  };

  // Don't render the header button if multi-account component is showing
  if (isMultiAccountComponentRendered) {
    return isMobile ? (
      <FormHeader>
        <FormHeaderTextWrapper
          isMobile={true}
          onClick={() => window.open("https://calebandbrown.com/", "_blank")}>
          <FormHeaderTextMobile>
            <CalebAndBrownLogo
              height={40}
              width={141}
              color="currentColor"
              aria-label="Caleb & Brown"
            />
          </FormHeaderTextMobile>
        </FormHeaderTextWrapper>
      </FormHeader>
    ) : (
      <FormHeader>
        <FormHeaderTextWrapper
          isMobile={false}
          onClick={() => window.open("https://calebandbrown.com/", "_blank")}>
          <FormHeaderText>
            <CalebAndBrownLogo
              height={40}
              width={141}
              color="currentColor"
              aria-label="Caleb & Brown"
            />
          </FormHeaderText>
        </FormHeaderTextWrapper>
      </FormHeader>
    );
  }

  return isMobile ? (
    <FormHeader>
      <FormHeaderTextWrapper
        isMobile={true}
        onClick={() => window.open("https://calebandbrown.com/", "_blank")}>
        <FormHeaderTextMobile>
          <CalebAndBrownLogo
            height={40}
            width={141}
            color="currentColor"
            aria-label="Caleb & Brown"
          />
        </FormHeaderTextMobile>
      </FormHeaderTextWrapper>
      <HeaderButton />
    </FormHeader>
  ) : (
    <FormHeader>
      <FormHeaderTextWrapper
        isMobile={false}
        onClick={() => window.open("https://calebandbrown.com/", "_blank")}>
        <FormHeaderText>
          <CalebAndBrownLogo
            height={40}
            width={141}
            color="currentColor"
            aria-label="Caleb & Brown"
          />
        </FormHeaderText>
      </FormHeaderTextWrapper>
      <HeaderButton />
    </FormHeader>
  );
};

const FormCallToActionComponent = () => {
  const { handleNext, handleLoginSubmit } = useLogin();
  const { step, isNextLoading, isSubmitLoading, error, resetCode } = useLoginFormInputSlice();
  const history = useHistory();

  return (
    <FormCallsToActionWrapper>
      {step === 1 ? (
        <StylisedButton
          isLoading={isNextLoading}
          isLoadingContents={<CircularProgress size={16} sx={{ color: "#FC4C4C" }} />}
          isDisabled={isNextLoading}
          onClick={handleNext}
          style={StylisedButtonStyle.SALMON}
          buttonText={"Next"}
        />
      ) : (
        <StylisedButton
          isLoading={isSubmitLoading}
          isLoadingContents={<CircularProgress size={16} sx={{ color: "#FC4C4C" }} />}
          isDisabled={isSubmitLoading}
          onClick={handleLoginSubmit}
          style={StylisedButtonStyle.SALMON}
          buttonText={"Login"}
        />
      )}

      {/* Only show password expired error here since it needs the reset link */}
      {error.length > 0 && error.toLowerCase().includes("password expired") && (
        <FormContentInputFieldError>
          <FormContentInputFieldErrorText>
            {error}
            <span
              style={{
                textDecoration: "underline",
                cursor: "pointer",
                color: "#002035",
              }}
              onClick={() => history.push(`/password/reset?code=${resetCode}`)}>
              {" "}
              here
            </span>
          </FormContentInputFieldErrorText>
        </FormContentInputFieldError>
      )}
    </FormCallsToActionWrapper>
  );
};

const FormFieldsComponent = () => {
  const { handleEmailChange, handlePasswordChange, setShowPassword, handleKeyDown } = useLogin();
  const { step, email, emailError, password, passwordError, showPassword } =
    useLoginFormInputSlice();
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === 2) passwordRef.current?.focus();
  }, [step]);

  return (
    <FormContentFields>
      <FormContentInputFieldWrapperStretch>
        <StylisedInputField
          value={email}
          onChange={handleEmailChange}
          isDisabled={false}
          placeholder="Email address"
          captionText="Email Address"
          endAdornment={<EnvelopeIcon />}
          errorMsg={emailError}
          onKeyDown={handleKeyDown}
        />
      </FormContentInputFieldWrapperStretch>

      {step === 2 && (
        <FormContentInputFieldWrapperStretch>
          <StylisedInputField
            value={password}
            onChange={handlePasswordChange}
            isDisabled={false}
            placeholder="Password"
            captionText="Password"
            inputRef={passwordRef}
            endAdornment={
              <div
                onClick={() => setShowPassword(!showPassword)}
                style={{ cursor: "pointer" }}
                className="password-toggle-icon">
                {showPassword ? <EyeOpenIcon /> : <EyeClosedIcon />}
              </div>
            }
            errorMsg={passwordError}
            type={showPassword ? "text" : "password"}
            onKeyDown={handleKeyDown}
          />
        </FormContentInputFieldWrapperStretch>
      )}

      {/* Bot check for the password step; the backend only enforces the token once
          AUTH_LOGIN_TURNSTILE_REQUIRED is on, so a blocked widget never locks users out
          before ops have confirmed it renders. */}
      {step === 2 && <TurnstileWidget />}
    </FormContentFields>
  );
};

const ForgotPasswordComponent = () => {
  const { trackForgotPasswordClick } = useLogin();

  return (
    <ForgotPasswordWrapper>
      <a
        href={"/reset/request"}
        onClick={trackForgotPasswordClick}
        style={{ color: "inherit", textDecoration: "inherit" }}>
        I forgot my password
      </a>
    </ForgotPasswordWrapper>
  );
};

interface TwoFAComponentProps {
  handleTwoFACodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleTwoFASubmit: () => void;
  handleTwoFABack: () => void;
}

const TwoFAComponent = ({ handleTwoFACodeChange, handleTwoFASubmit, handleTwoFABack }: TwoFAComponentProps) => {
  const { twoFACode, twoFAError, isTwoFALoading } = useLoginFormInputSlice();
  const twoFARef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    twoFARef.current?.focus();
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && twoFACode.length === 6) {
      event.preventDefault();
      handleTwoFASubmit();
    }
  };

  return (
    <FormContentLeftAligned>
      <FormContentTitle>Two Factor Authentication</FormContentTitle>
      <FormContentFields>
        <FormContentInputFieldWrapperStretch>
          <StylisedInputField
            value={twoFACode}
            onChange={handleTwoFACodeChange}
            isDisabled={isTwoFALoading}
            placeholder="Enter 6-digit code"
            captionText=""
            errorMsg={twoFAError}
            onKeyDown={handleKeyDown}
            inputRef={twoFARef}
          />
          {!twoFAError && (
            <TwoFASubtitle>You can retrieve the code from your Authenticator App</TwoFASubtitle>
          )}
        </FormContentInputFieldWrapperStretch>
      </FormContentFields>
      <FormCallsToActionWrapper>
        <StylisedButton
          isLoading={isTwoFALoading}
          isLoadingContents={<CircularProgress size={16} sx={{ color: "#FC4C4C" }} />}
          isDisabled={isTwoFALoading || twoFACode.length !== 6}
          onClick={handleTwoFASubmit}
          style={StylisedButtonStyle.SALMON}
          buttonText={"Confirm"}
        />
        <StylisedButton
          isLoading={false}
          isDisabled={isTwoFALoading}
          onClick={handleTwoFABack}
          style={StylisedButtonStyle.OUTLINE}
          buttonText={"Go back"}
        />
      </FormCallsToActionWrapper>
    </FormContentLeftAligned>
  );
};

const FormInitialInputFormComponent = () => {
  const isMobile = useIsMobile();

  return isMobile ? (
    <>
      <FormContentTitleAndFieldsWrapper>
        <FormContentTitleAndFieldsMobile>
          <FormContentTitle>Login</FormContentTitle>
          <FormFieldsComponent />
        </FormContentTitleAndFieldsMobile>
        <ForgotPasswordComponent />
      </FormContentTitleAndFieldsWrapper>
      <FormCallToActionComponent />
    </>
  ) : (
    <FormContent>
      <FormContentTitleAndFieldsWrapper>
        <FormContentTitleAndFields>
          <FormContentTitle>Login</FormContentTitle>
          <FormFieldsComponent />
        </FormContentTitleAndFields>
        <ForgotPasswordComponent />
      </FormContentTitleAndFieldsWrapper>
      <FormCallToActionComponent />
    </FormContent>
  );
};

// const MultiAccountComponent = ({
//   emailList,
//   handleAccountChange,
// }: {
//   emailList: GetMultiAccountUsersApiResponse;
//   handleAccountChange: (customerId: string) => void;
// }) => {
//   const dispatch = useAppDispatchStore();

//   // Set number of accounts
//   useEffect(() => {
//     dispatch(setNumberOfAccounts(emailList.length || 1));
//   }, [emailList.length, dispatch]);

//   // Set multi account component rendered flag
//   useEffect(() => {
//     dispatch(setIsMultiAccountComponentRendered(true));

//     // Cleanup: set to false when component unmounts
//     return () => {
//       dispatch(setIsMultiAccountComponentRendered(false));
//     };
//   }, [dispatch]);

//   // If any account has default=true, hide the component (redirect will happen)
//   const hasDefaultAccount = emailList.some((account) => account.default);
//   if (hasDefaultAccount) {
//     return <></>;
//   }

//   return (
//     <FormContentLeftAligned>
//       <FormContentTitle>Select Account</FormContentTitle>
//       <FormContentFields>
//         <FormContentInputFieldWrapperStretch>
//           <MultiAccountDescription>
//             Please select the account you would like to login with below
//           </MultiAccountDescription>
//           <MultiAccountOptionsContainer>
//             {emailList?.map((element, index) => (
//               <MultiAccountOptionButton
//                 key={index}
//                 onClick={() => handleAccountChange(element.customerId)}>
//                 <MultiAccountOptionContent>
//                   <MultiAccountOptionText>
//                     <MultiAccountOptionName>{element.name}</MultiAccountOptionName>
//                     <MultiAccountOptionId>{element.customerId}</MultiAccountOptionId>
//                   </MultiAccountOptionText>
//                 </MultiAccountOptionContent>
//               </MultiAccountOptionButton>
//             ))}
//           </MultiAccountOptionsContainer>
//         </FormContentInputFieldWrapperStretch>
//       </FormContentFields>
//     </FormContentLeftAligned>
//   );
// };

const FormComponent = () => {
  const isMobile = useIsMobile();
  const { magicLink, showTwoFA } = useLoginFormInputSlice();
  const { handleTwoFACodeChange, handleTwoFASubmit, handleTwoFABack } = useLogin();

  const Content = () => {
    if (showTwoFA) {
      return <TwoFAComponent handleTwoFACodeChange={handleTwoFACodeChange} handleTwoFASubmit={handleTwoFASubmit} handleTwoFABack={handleTwoFABack} />;
    } else if (magicLink) {
      return <MagicLinkForm />;
    }
    return <FormInitialInputFormComponent />;
  };

  return isMobile ? (
    <FormFrameMobile>{Content()}</FormFrameMobile>
  ) : (
    <FormFrame>
      <FormHeaderComponent />
      {Content()}
    </FormFrame>
  );
};

export function LandingPageLogin() {
  const isMobile = useIsMobile();
  const { loading } = useLoginFormInputSlice();

  if (loading) {
    return (
      <>
        {isMobile ? (
          <LoadingSpinnerContainer>
            <LoadingSpinner size={60} />
          </LoadingSpinnerContainer>
        ) : (
          <FormWrapper item xs={6}>
            <LoadingSpinnerContainer>
              <LoadingSpinner size={60} />
            </LoadingSpinnerContainer>
          </FormWrapper>
        )}
      </>
    );
  }

  return (
    <>
      {isMobile ? (
        <>
          <FormHeaderComponent />
          <FormComponent />
          <PublicIntercom />
        </>
      ) : (
        <FormWrapper item xs={6}>
          <FormComponent />
          <PublicIntercom />
        </FormWrapper>
      )}
    </>
  );
}
